/**
 * LockReconciliation.tsx — what happens when a regeneration meets a lock.
 *
 * THE PRODUCT RULE, and the reason this component exists rather than a toast:
 *
 *   A regeneration that wants to move locked geometry must SHOW the exact lock,
 *   must NOT silently skip it, must NOT overwrite it, and must NOT proceed
 *   without an explicit reviewed resolution.
 *
 * Each of those four is a failure a browser is uniquely good at causing, because
 * a browser is where partial information and a tempting default button live. So
 * the guarantees are structural:
 *
 *   • NOT SILENTLY SKIPPED. Every lock the Engine reports is listed, by the
 *     Engine's own identity, in the same list as everything else on the page.
 *     There is no "hide resolved" and no default state that quietly leaves a lock
 *     out of the picture.
 *   • NOT OVERWRITTEN. This component offers NO control that releases, force-
 *     releases, or overrides a lock. It has no such button, so the failure is
 *     unrepresentable rather than merely discouraged. Releasing a lock is a
 *     reviewed decision recorded by the Engine, and a browser-side button would
 *     be a second, unreviewed way to take that decision.
 *   • EXPLICIT REVIEWED RESOLUTION. Staging is blocked while any lock is
 *     unresolved. The only way forward is the Engine's own resolution, reached
 *     through the same reviewed path as any other change — which is why the panel
 *     POINTS at the Engine's lock capabilities rather than offering to do it.
 *   • THE EXACT GOVERNING LOCK AND THE EXACT CONFLICT. Each row names the lock id,
 *     the domain, the subject and the Engine's own reason, and the panel says
 *     where in the Engine's answer it found it. A reviewer reconciling a
 *     regeneration needs to be able to go and look at the same thing.
 *
 * SCHEMATIC AND PHYSICAL ARE NOT THE SAME FACT
 * ---------------------------------------------
 * A lock on a manual placement in a panel governs real millimetres in a real
 * assembly. A lock on a route drawn on a sheet governs a drawing. They are the
 * same SHAPE, and merging them would let a reviewer read a schematic lock as
 * though it protected a physical coordinate — which is precisely the confusion
 * that makes a physical panel untrustworthy. So the Engine's domain is carried
 * through verbatim, locks are grouped by it, and the browser states plainly that
 * it does not know which frame any of them governs. The Engine owns that.
 */

import { useMemo } from "react";
import { Empty, Panel } from "../../components/Panel";
import type { LockVerdict, ReportedLock } from "./lockReconciliation";

export { lockVerdict, readLocks } from "./lockReconciliation";
export type { LockVerdict, ReportedLock } from "./lockReconciliation";

/**
 * Whether the Engine reported a lock whose domain this repository has a different
 * WORD for. It does not: the domain is the Engine's, and this function exists
 * only to prove the panel never branches on it.
 *
 * Present as a function so the claim is used rather than asserted in a comment —
 * a comment is a claim nothing checks, and this one is the claim most likely to
 * be quietly broken by a well-meaning "let's colour schematic locks blue".
 */
export function domainMeansAnythingToThisBrowser(domain: string): false {
  void domain;
  return false;
}

function LockRow({ lock }: { lock: ReportedLock }): JSX.Element {
  return (
    <li className={lock.resolution === undefined ? "lock-unresolved" : undefined} data-lock={lock.lockId} data-domain={lock.domain}>
      <code>{lock.lockId}</code>
      <span className="muted">
        domain <code>{lock.domain}</code> · subject <code>{lock.subjectId}</code> · found at <code>{lock.path}</code>
      </span>
      {lock.reason !== undefined && <span>{lock.reason}</span>}
      {lock.resolution !== undefined && <span className="good">The Engine resolved it: {lock.resolution}</span>}
    </li>
  );
}

function GroupedByDomain({ locks, heading }: { locks: readonly ReportedLock[]; heading: string }): JSX.Element {
  // Grouping is presentation. The domain is the Engine's own text, and this
  // component does not map it onto a frame, a discipline or a coordinate system.
  const byDomain = new Map<string, ReportedLock[]>();
  for (const lock of locks) byDomain.set(lock.domain, [...(byDomain.get(lock.domain) ?? []), lock]);
  return (
    <>
      <h3>{heading}</h3>
      {[...byDomain.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([domain, entries]) => (
        <div key={domain} className="lock-domain" data-domain={domain}>
          <h4>
            The Engine&apos;s domain <code>{domain}</code> <span className="muted">({entries.length})</span>
          </h4>
          <p className="muted">
            This browser does not know which frame this domain governs, and will not say: the Engine owns that
            decision, and a client that guessed would let a reviewer read one kind of lock as though it protected
            another.
          </p>
          <ul className="lock-list">{entries.map(lock => <LockRow key={lock.lockId} lock={lock} />)}</ul>
        </div>
      ))}
    </>
  );
}

export function LockReconciliation({ verdict, disabled }: { verdict: LockVerdict; disabled?: boolean }): JSX.Element | null {
  const summary = useMemo(() => {
    if (verdict.unresolved.length > 0) {
      return `${verdict.unresolved.length} lock${verdict.unresolved.length === 1 ? "" : "s"} the Engine reported and nobody has resolved. `
        + "This change cannot be staged until each is resolved through the Engine.";
    }
    if (verdict.resolved.length > 0) {
      return "Every lock the Engine reported carries its own resolution. Nothing was skipped and nothing was overwritten.";
    }
    return "The Engine's result carried no lock. That is a statement about this result, not a claim that nothing is locked.";
  }, [verdict]);

  if (!verdict.reported) return null;

  return (
    <Panel title="Locks" testId="stage-locks" tone={verdict.blocksApply ? "warn" : "default"}>
      <p className={verdict.blocksApply ? "bad" : "muted"}>{summary}</p>
      <p className="muted" data-stage="lock-domains">
        Domains the Engine reported:{" "}
        {verdict.domains.length === 0
          ? "none"
          : verdict.domains.map(domain => <code key={domain} data-domain={domain}>{domain}</code>).reduce<React.ReactNode[]>(
              (acc, node, index) => (index === 0 ? [node] : [...acc, ", ", node]),
              [],
            )}
      </p>

      {verdict.unresolved.length > 0 && (
        <>
          <GroupedByDomain locks={verdict.unresolved} heading="Unresolved — staging is blocked" />
          <p className="bad">
            Staging is blocked. This screen deliberately offers no way to release, force or override a lock: releasing
            one is a reviewed decision, and the Engine owns how it is recorded. Take the resolution to the Engine
            through the ordinary reviewed path, then re-run this preview against the new state.
          </p>
        </>
      )}

      {verdict.resolved.length > 0 && <GroupedByDomain locks={verdict.resolved} heading="Resolved by the Engine" />}

      {verdict.refusals.length > 0 && (
        <>
          <h3>The Engine&apos;s own words</h3>
          <ul className="findings" data-stage="lock-refusals">
            {verdict.refusals.map((text, index) => <li key={index}><span>{text}</span></li>)}
          </ul>
        </>
      )}

      {disabled && <p className="muted">Blocked while another operation is in flight.</p>}
    </Panel>
  );
}

export function NoLocksReported(): JSX.Element {
  return (
    <Panel title="Locks" testId="stage-locks">
      <Empty>
        This result reported no lock. The Engine is the only party that can say whether geometry is protected, and it
        reported nothing here — which is not the same as there being no lock.
      </Empty>
    </Panel>
  );
}
